import { FB_PIXEL_IDS, GTM_IDS, GTAG_IDS } from './Branding';

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: unknown;
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

/** Meta's standard Pixel base code, verbatim — defines window.fbq, loads fbevents.js once, then
 * inits every configured pixel ID against that single shared instance. */
function loadFacebookPixel(pixelIds: string[]) {
  (function (f: any, b: Document, e: string, v: string, n: any, t: any, s: any) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js', undefined, undefined, undefined);
  for (const pixelId of pixelIds) {
    window.fbq!('init', pixelId);
  }
  window.fbq!('track', 'PageView');
}

/** GTM's standard container snippet, verbatim — one gtm.js load per container, since each
 * container id drives its own script src (unlike gtag.js, GTM has no shared-loader form). */
function loadGoogleTagManager(gtmIds: string[]) {
  for (const gtmId of gtmIds) {
    (function (w: any, d: Document, s: string, l: string, i: string) {
      w[l] = w[l] || [];
      w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      const f = d.getElementsByTagName(s)[0];
      const j = d.createElement(s) as HTMLScriptElement;
      const dl = l !== 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode!.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', gtmId);
  }
}

/** Google's standard gtag.js snippet — a single script load (keyed off the first id) defines
 * window.gtag/dataLayer, then every configured id (GA4 "G-", Ads "AW-", Floodlight "DC-", etc.)
 * gets its own gtag('config', id) call re-using that one script load, per Google's own documented
 * multi-property pattern. */
function loadGtag(gtagIds: string[]) {
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtag/js?id=' + gtagIds[0];
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  for (const gtagId of gtagIds) {
    window.gtag('config', gtagId);
  }
}

/** Initializes this brand's Facebook Pixel(s) / GTM container(s) / gtag id(s), if configured —
 * sourced from whitelabelEnv.ts (FB_PIXEL_IDS/GTM_IDS/GTAG_IDS), not hardcoded per-hostname in
 * index.html. No-ops for brands that don't have any set. */
export function initBrandTracking() {
  if (FB_PIXEL_IDS.length > 0) loadFacebookPixel(FB_PIXEL_IDS);
  if (GTM_IDS.length > 0) loadGoogleTagManager(GTM_IDS);
  if (GTAG_IDS.length > 0) loadGtag(GTAG_IDS);
}
